import { Grid, Typography, Stack, Button, IconButton, Box, TextField, Divider, Paper } from '@mui/material';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import '../StudyLogs.scss';

import React, { useEffect, useState } from 'react';
import printPdfWithViewer from 'src/components/common/PrintPdfWithViewer';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import { UI_TIME_ZONE } from 'src/constants/formats';
import { useAppSelector } from 'src/store';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch } from 'src/store/hooks';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { useConfirmationVisit } from './useConfirmationLetter';
import { MainRTE } from '@pihealth/common-ui-library';
import StaticJson from '../../../../resources/static.json';
import { archLevel } from 'src/data/config';

const InterimMonitoringVisitConfirmationLetter: React.FunctionComponent<any> = ({
  visitData,
  SiteStudyData,
  setIsChanged,
  isChanged
}) => {
  const [salutation, setSalutation] = useState('');
  const [isContentEdit, setIsContentEdit] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [error, setError] = useState(false);
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const sponsor = useAppSelector(state => state.user.user?.sponsor);
  const email = useAppSelector(state => state.user.user?.email);
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const { checkPermission } = usePermissionWrapper();
  const dispatch = useAppDispatch();
  const { getPreferredDateTimeInLocal } = usePreferedDateFormat();
  const contentEditPermission = checkPermission('edit_imv_confirmation_letter');
  const { user: userFullName } = useUser();
  const [htmlContent, setHtmlContent] = useState('');
  const [openRTE, setOpenRTE] = useState(false);
  const [openRTEContent, setOpenRTEContent] = useState(false);
  const [disableEditContent, setDisableEditContent] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const {
    confirmationvisitLetterData,
    confirmationvisitLetterError,
    confirmationvisitLetterIsError,
    confirmationvisitLetterIsLoading,
    confirmationvisitLetterIsSuccess,
    getConfirmationvisitLetter,
    createConfirmationvisitError,
    createConfirmationvisitIsError,
    createConfirmationvisitIsLoading,
    createConfirmationvisitIsSuccess,
    createConfirmationvisit,
    updateConfirmationvisitError,
    updateConfirmationvisitIsError,
    updateConfirmationvisitIsLoading,
    updateConfirmationvisitIsSuccess,
    updateConfirmationvisit,
    publishConfirmationvisitError,
    publishConfirmationvisitIsError,
    publishConfirmationvisitIsSuccess,
    publishConfirmationvisit
  } = useConfirmationVisit();

  const isUpdatePublish = confirmationvisitLetterData?.stdy_imvc_publish_version ? true : false;

  useEffect(() => {
    setIsChanged(false);
    getConfirmationvisitLetter({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME,
      monitoring_visit_id: visitData.id
    });
  }, [
    currentHierarchyLevel,
    studyId,
    selectedCountry,
    selectedSite,
    createConfirmationvisitIsSuccess,
    updateConfirmationvisitIsSuccess,
    publishConfirmationvisitIsSuccess
  ]);

  useEffect(() => {
    if (createConfirmationvisitIsSuccess) {
      setDisableSave(true);
      setDisableEditContent(false);
      dispatch(
        setMessage({
          msgDetails: 'Data saved successfully',
          severity: 'success'
        })
      );
    }
    if (updateConfirmationvisitIsSuccess) {
      setDisableSave(true);
      setDisableEditContent(false);
      dispatch(
        setMessage({
          msgDetails: 'Data updated successfully',
          severity: 'success'
        })
      );
    }
    if (publishConfirmationvisitIsSuccess) {
      dispatch(
        setMessage({
          msgDetails: 'IMV Confirmation Letter successfully published to eTMF repository',
          severity: 'success'
        })
      );
    }
    setConfirmExitValue(dispatch, false);
    setIsContentEdit(true);
  }, [createConfirmationvisitIsSuccess, updateConfirmationvisitIsSuccess, publishConfirmationvisitIsSuccess]);

  useEffect(() => {
    if (confirmationvisitLetterIsLoading || createConfirmationvisitIsLoading || updateConfirmationvisitIsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [confirmationvisitLetterIsLoading, createConfirmationvisitIsLoading, updateConfirmationvisitIsLoading]);

  useEffect(() => {
    let err: any = {};

    if (updateConfirmationvisitIsError) {
      err = updateConfirmationvisitError;
    } else if (createConfirmationvisitIsError) {
      err = createConfirmationvisitError;
    } else if (confirmationvisitLetterIsError) {
      err = confirmationvisitLetterError;
    } else if (publishConfirmationvisitIsError) {
      err = publishConfirmationvisitError;
    }

    if (err?.status == 500) {
      dispatch(
        setMessage({
          msgDetails: `${err?.status} : Internal Server Error`,
          severity: 'error'
        })
      );
    } else if (err?.status == 403) {
      dispatch(
        setMessage({
          msgDetails: 'Error 403: Restricted User permission',
          severity: 'error'
        })
      );
    } else if (err?.status == 404) {
      return;
    }
  }, [
    createConfirmationvisitIsError,
    createConfirmationvisitError,
    updateConfirmationvisitError,
    updateConfirmationvisitIsError,
    confirmationvisitLetterIsError,
    confirmationvisitLetterError,
    publishConfirmationvisitIsError
  ]);

  const printIMVCL = async () => {
    try {
      dispatch(showLoader());
      const options = {
        sq_visit_id: visitData?.id,
        template_name: 'Interim_Monitoring_Visit_Confirmation_Letter',
        study_id: studyId,
        site_id: selectedSite,
        pathVariables: {
          time_zone_id: UI_TIME_ZONE,
          country: selectedCountry,
          appName: process.env.REACT_APP_NAME
        }
      };
      let pdfOptions: any = {};
      if (confirmationvisitLetterData.stdy_imvc_publish_version) {
        pdfOptions = {
          ...options,
          pathVariables: {
            version: confirmationvisitLetterData.stdy_imvc_publish_version.toFixed(1),
            ...options.pathVariables
          }
        };
      } else {
        pdfOptions = options;
      }
      const printViewerApiString = confirmationvisitLetterData.stdy_imvc_publish_version
        ? 'get_imvconfirmationletter_pdf_file_version'
        : 'get_imvconfirmationletter_pdf_file';

      printPdfWithViewer(printViewerApiString, pdfOptions, 'application/pdf');
      setTimeout(() => {
        dispatch(hideLoader());
      }, 1500);
    } catch (error: any) {
      dispatch(hideLoader());
      dispatch(setMessage({ msgDetails: 'Error while fetching pdf', severity: 'error' }));
    }
  };

  const dateTime = getPreferredDateTimeInLocal(visitData?.start_date).split(' ');
  const date = dateTime[0];
  const time = dateTime[1];
  const meridiem = dateTime[2];

  const phoneNum = SiteStudyData?.data?.personnel
    .map((items: { site_per_email: string | undefined; site_per_phone: any }) => {
      if (items.site_per_email?.toLowerCase() === email?.toLowerCase()) {
        return items.site_per_phone;
      }
    })
    .filter((item: any) => {
      if (item !== undefined) {
        return item;
      }
    });

  const [details, setDetails] = useState({
    phoneNo: phoneNum[0],
    email,
    name: userFullName
  });

  const validationOfRTE =
    confirmationvisitLetterData === undefined ||
    confirmationvisitLetterData?.imvcl_rte === '' ||
    confirmationvisitLetterData?.imvcl_rte === null
      ? StaticJson.IMVconfirmationLetterRTE1
      : confirmationvisitLetterData?.imvcl_rte;

  const handleSave = async () => {
    if (!salutation) {
      setError(true);
    } else {
      const data = {
        stdy_imvc_salutation_letter: salutation.trim(),
        stdy_imvc_phone_number_letter: details.phoneNo,
        stdy_imvc_email_address: email,
        stdy_imvc_cra_first_name: details.name,
        stdy_imvc_name_sponsor: sponsor,
        stdy_imvc_publish_version: confirmationvisitLetterData?.stdy_imvc_publish_version,
        monitoring_visit_id: visitData?.id,
        is_published: false,
        imvcl_rte: htmlContent === '' ? validationOfRTE : htmlContent
      };

      if (isUpdate) {
        if (salutation === confirmationvisitLetterData.stdy_imvc_salutation_letter && !isContentEdit) {
          dispatch(setMessage({ msgDetails: 'No data modified', severity: 'success' }));
        } else {
          updateConfirmationvisit({
            site_id: selectedSite,
            study_id: studyId,
            country: selectedCountry,
            application: process.env.REACT_APP_NAME,
            data: {
              ...data,
              updated_by: userFullName,
              tp_document_meta_id: confirmationvisitLetterData.tp_document_meta_id,
              tp_document_id: confirmationvisitLetterData.tp_document_id
            }
          });
        }
      } else {
        createConfirmationvisit({
          site_id: selectedSite,
          study_id: studyId,
          country: selectedCountry,
          application: process.env.REACT_APP_NAME,
          data: { ...data, created_by: userFullName }
        });
      }
      setError(false);
      setIsChanged(false);
      setOpenRTE(false);
      setDisableEditContent(true);
      setOpenRTEContent(false);
    }
  };
  const handleChange = (e: any) => {
    const inputValue = e.target.value;
    setIsChanged(true);
    setDisableSave(true);
    setSalutation(inputValue.trimStart());
    setConfirmExitValue(dispatch, true);
  };

  const handlePublish = async () => {
    dispatch(showLoader());
    let version = 0;
    if (isUpdatePublish) {
      version = Math.floor(confirmationvisitLetterData?.stdy_imvc_publish_version + 1);
    } else {
      version = 1;
    }
    await publishConfirmationvisit({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME,
      publish_doc_version: version,
      monitoring_visit_id: visitData?.id,
      time_zone_id: UI_TIME_ZONE,
      update: isUpdatePublish
    });
    setConfirmExitValue(dispatch, false);
    setIsChanged(false);
    setDisableEditContent(true);
    setDisableSave(true);
    dispatch(hideLoader());
  };

  useEffect(() => {
    if (confirmationvisitLetterIsSuccess) {
      setIsUpdate(true);
      setSalutation(confirmationvisitLetterData.stdy_imvc_salutation_letter);
      setDetails({
        phoneNo: confirmationvisitLetterData.stdy_imvc_phone_number_letter,
        email: confirmationvisitLetterData.stdy_imvc_email_address,
        name: confirmationvisitLetterData.stdy_imvc_cra_first_name
      });
    }
  }, [confirmationvisitLetterIsSuccess]);

  const handleChangeTextEditor = (htmlContext: any, _: any) => {
    setIsContentEdit(true);
    setHtmlContent(htmlContext);
  };

  const handleEditRTE = (_: string) => {
    setOpenRTE(true);
    setOpenRTEContent(true);
  };

  const handleSaveRTE = (_: string) => {
    setOpenRTE(false);
    setDisableEditContent(true);
    setIsChanged(true);
    setDisableSave(true);
  };

  return (
    <>
      <Grid container spacing={1} sx={{ mb: 1.5, alignItems: 'center' }}>
        <Grid item>
          <Typography variant="h5" component="h5" sx={{ margin: 0, fontSize: '20px', fontWeight: 500 }}>
            {visitData.visit_name}
          </Typography>
        </Grid>
        <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <Stack direction="row" spacing={1.5}>
            {archLevel === 'sponsor' && (
              <Button
                variant="contained"
                style={{ textTransform: 'none' }}
                sx={{ alignSelf: 'center' }}
                disabled={
                  (!checkPermission('publish_imvconfirmationletter') &&
                    !checkPermission('update_imvconfirmationletter')) ||
                  (confirmationvisitLetterData?.is_published === undefined
                    ? true
                    : confirmationvisitLetterData?.is_published)
                }
                onClick={handlePublish}
              >
                PUBLISH TO eTMF
              </Button>
            )}
            {archLevel === 'sponsor' && (
              <Button
                sx={{ alignSelf: 'center' }}
                onClick={handleSave}
                disabled={!checkPermission('update_imvconfirmationletter') || !isChanged || !disableSave || !salutation}
              >
                Save
              </Button>
            )}

            <IconButton
              aria-label="Print"
              color="primary"
              sx={{ alignSelf: 'center' }}
              onClick={() => printIMVCL()}
              disabled={!confirmationvisitLetterData?.is_published}
            >
              <LocalPrintshopIcon />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>

      <Box className="letterPage">
        {SiteStudyData && (
          <Typography gutterBottom color="text.secondary2">
            {date}
            <br />
            {SiteStudyData?.data.site?.pi_first_name} {SiteStudyData?.data.site?.pi_last_name}
            <br />
            {SiteStudyData?.data.site?.site_name} <br />
            {SiteStudyData?.data.site?.site_street_address1}, {SiteStudyData?.data.site?.site_street_address2} <br />
            {SiteStudyData?.data.site?.site_city}, {SiteStudyData?.data.site?.site_state},{' '}
            {SiteStudyData?.data.site?.site_zip_code}
          </Typography>
        )}

        <dl className="text-Bold dlStyle">
          <dt>RE</dt>
          <dd>
            {SiteStudyData?.data.study?.study_id}-{SiteStudyData?.data.study?.study_title} <br />
            Interim Monitoring Visit for Protocol {SiteStudyData?.data.study?.study_id}
            <br />
            Site Number: {SiteStudyData?.data.site?.site_id}
          </dd>
        </dl>

        <Typography sx={{ mb: 1, pt: 2 }}>
          Dear &nbsp;{' '}
          <span style={{ width: '220px', display: 'inline-block' }}>
            <TextField
              id="salutationField"
              label="Salutation"
              required
              variant="standard"
              className="salutationField"
              value={salutation?.trimStart()}
              disabled={!checkPermission('update_imvconfirmationletter')}
              onChange={e => handleChange(e)}
              inputProps={{ maxLength: 50 }}
              helperText={error ? 'Required' : ''}
              error={error}
            />
          </span>
          {''}
          {SiteStudyData?.data.site?.pi_first_name} {SiteStudyData?.data.site?.pi_last_name}
          ,<br />
        </Typography>
        <Typography gutterBottom>
          This letter is to confirm my appointment for a visit to your site on {date}. I should arrive at approximately{' '}
          {time} {meridiem}.
        </Typography>
        {contentEditPermission ? (
          <Paper variant="outlined" sx={{ position: 'relative', my: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 1.5, px: 2 }}>
              <Typography variant="subtitle1">Editable Content</Typography>
              {openRTE ? (
                <Button variant="contained" size="small" color="primary" onClick={() => handleSaveRTE('content')}>
                  SAVE
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  disabled={disableEditContent}
                  onClick={() => handleEditRTE('content')}
                >
                  EDIT
                </Button>
              )}
            </Box>
            <Divider variant="middle" sx={{ mx: 0 }} />
            <Box className="editRTEBox" p={2}>
              {openRTEContent ? (
                <MainRTE
                  log={(htmlContext: any, planetext: any) => handleChangeTextEditor(htmlContext, planetext)}
                  value={validationOfRTE}
                  placeholder={'Enter Value'}
                  height={400}
                  disabled={disableEditContent}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: validationOfRTE
                  }}
                />
              )}
            </Box>
          </Paper>
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: validationOfRTE
            }}
          />
        )}
        <Typography gutterBottom>
          I will meet with you via phone, subject to your availability, on {date} to discuss any issues and to review
          findings. If you have any questions, please do not hesitate to contact me at {details.phoneNo},{' '}
          {details.email}.
        </Typography>

        <Typography gutterBottom>
          Yours sincerely, <br />
          {details.name}
          <br />
          <span color="text.secondary">{sponsor?.toUpperCase()}</span>
        </Typography>
      </Box>
    </>
  );
};
export default InterimMonitoringVisitConfirmationLetter;
